import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { StateController } from '../state';
import type { ScheduledTask } from '../entities';

@customElement('umb-market-tasks-timer')
export class UmbMarketTasksTimer extends LitElement {

  #stateController = new StateController(this);

  @state()
  private _scheduledTasks?: Array<ScheduledTask> | null;

  async connectedCallback() {
    super.connectedCallback();

    this._scheduledTasks = this.#stateController.state.scheduledTasks;

    if (StateController.current.isBackoffice) {
      this.setAttribute(
        'backoffice', ''
      );
    }
  }

  #getRelativeDate = (lastStartedDate: string) => {
    const diff = new Date(lastStartedDate).getTime() - Date.now();
    const seconds = diff / 1000;
    const decimalMinutes = seconds / 60;

    // round minutes to nearest 15
    const roundedMinutes = Math.round(decimalMinutes / 15) * 15;

    // convert back to hours and minutes
    const hours = Math.abs(Math.floor(roundedMinutes / 60));
    const minutes = Math.abs(roundedMinutes % 60);

    // check if the difference is greater than 24 hours
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;

      if (remainingHours === 0) {
        return `${days} day${days === 1 ? '' : 's'} ago`;
      }

      return `${days} day${days === 1 ? '' : 's'}, ${remainingHours} hour${
        remainingHours === 1 ? '' : 's'
      } ago`;
    }

    // format the returned string
    if (hours === 0) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }

    if (minutes === 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    return `${hours} hour${hours === 1 ? '' : 's'} and ${minutes} minute${
      minutes === 1 ? '' : 's'
    } ago`;
  };

  render() {
    return html` <ul>
      ${this._scheduledTasks?.map(
        task =>
          html`<li>
            ${task.name}:
            ${when(
              task.lastStarted,
              () => html`${this.#getRelativeDate(task.lastStarted!)}`,
              () => html`No last run time found`
            )}
          </li>`
      )}
    </ul>`;
  }

  static styles = [
    css`
      :host {
        color: var(--uui-color-text);
      }

      :host([backoffice]) {
        background-color: white;
      }

      ul {
        padding: 0;
        margin: 0;
        list-style-type: none;
      }
    `,
  ];
}
